import React from "react";
import {
  Tooltip as AriaTooltip,
  type TooltipProps as AriaTooltipProps,
  composeRenderProps,
  OverlayArrow,
  TooltipTrigger,
  type TooltipTriggerComponentProps,
} from "react-aria-components";

/**
 * HoverCard
 *
 * Rich content popover that appears on hover, built on `react-aria-components` TooltipTrigger.
 * Unlike Tooltip, HoverCard supports interactive content and complex layouts.
 *
 * **Important**: HoverCard requires exactly two children:
 * 1. A trigger element (button, link, or any focusable element)
 * 2. A `HoverCard.Content` component containing the popup content
 *
 * @example
 * ```tsx
 * <HoverCard>
 *   <Button>Hover me</Button>
 *   <HoverCard.Content showArrow>
 *     <div>Rich content here</div>
 *   </HoverCard.Content>
 * </HoverCard>
 * ```
 */
export interface HoverCardRootProps extends Omit<TooltipTriggerComponentProps, "children"> {
  /**
   * Exactly two children required:
   * 1. Trigger element (button, link, etc.)
   * 2. HoverCard.Content component
   */
  children: React.ReactNode;
  /**
   * Delay before showing the hover card (in milliseconds)
   * @default 700
   */
  delay?: number;
  /**
   * Delay before closing the hover card (in milliseconds)
   * @default 0
   */
  closeDelay?: number;
  /**
   * Whether the hover card is open (controlled mode)
   */
  isOpen?: boolean;
  /**
   * The default open state when initially rendered (uncontrolled mode)
   * Use when you don't need to control the open state
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * Handler called when the open state changes
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Whether the hover card is disabled and cannot be opened
   * @default false
   */
  isDisabled?: boolean;
}

export interface HoverCardContentProps extends Omit<AriaTooltipProps, "children"> {
  /**
   * Content to display in the hover card
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes (can be a string or render prop function)
   */
  className?: AriaTooltipProps["className"];
  /**
   * Whether to show an arrow pointing to the trigger
   * @default false
   */
  showArrow?: boolean;
}

function getHoverCardStyles({
  isEntering,
  isExiting,
  className = "",
}: {
  isEntering?: boolean;
  isExiting?: boolean;
  className?: string;
}) {
  const baseStyles = [
    "group",
    "bg-background-surface",
    "forced-colors:bg-[Canvas]",
    "text-text-primary",
    "shadow-lg",
    "rounded-[var(--control-border-radius)]",
    "bg-clip-padding",
    "border",
    "border-border-default",
    "outline-none",
    "p-4",
    "min-w-[260px]",
    "max-w-[360px]",
  ]
    .filter(Boolean)
    .join(" ");

  const enteringStyles = isEntering
    ? [
        "animate-in",
        "fade-in",
        "zoom-in-95",
        "placement-bottom:slide-in-from-top-1",
        "placement-top:slide-in-from-bottom-1",
        "placement-left:slide-in-from-right-1",
        "placement-right:slide-in-from-left-1",
        "ease-out",
        "duration-200",
      ].join(" ")
    : "";

  const exitingStyles = isExiting
    ? [
        "animate-out",
        "fade-out",
        "zoom-out-95",
        "placement-bottom:slide-out-to-top-1",
        "placement-top:slide-out-to-bottom-1",
        "placement-left:slide-out-to-right-1",
        "placement-right:slide-out-to-left-1",
        "ease-in",
        "duration-150",
      ].join(" ")
    : "";

  return [baseStyles, enteringStyles, exitingStyles, className].filter(Boolean).join(" ");
}

const HoverCardRoot = ({
  children,
  delay = 700,
  closeDelay = 0,
  isOpen,
  defaultOpen,
  onOpenChange,
  isDisabled = false,
  ...props
}: HoverCardRootProps) => (
  <TooltipTrigger
    delay={delay}
    closeDelay={closeDelay}
    isOpen={isOpen}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
    isDisabled={isDisabled}
    {...props}
  >
    {children}
  </TooltipTrigger>
);

const HoverCardContent = ({ children, className, showArrow = false, offset, ...props }: HoverCardContentProps) => {
  const calculatedOffset = offset ?? (showArrow ? 12 : 8);

  return (
    <AriaTooltip
      {...props}
      offset={calculatedOffset}
      className={composeRenderProps(className, (className, renderProps) =>
        getHoverCardStyles({ ...renderProps, className })
      )}
    >
      {showArrow && (
        <OverlayArrow>
          <svg
            width={12}
            height={12}
            viewBox="0 0 12 12"
            className="block fill-background-surface stroke-border-default stroke-1 group-placement-left:-rotate-90 group-placement-right:rotate-90 group-placement-bottom:rotate-180 forced-colors:fill-[Canvas] forced-colors:stroke-[ButtonBorder]"
          >
            <path d="M0 0 L6 6 L12 0" />
          </svg>
        </OverlayArrow>
      )}
      {children}
    </AriaTooltip>
  );
};

HoverCardContent.displayName = "HoverCardContent";

export type HoverCardComponent = typeof HoverCardRoot & {
  Root: typeof HoverCardRoot;
  Content: typeof HoverCardContent;
};

export const HoverCard: HoverCardComponent = Object.assign(HoverCardRoot, {
  Root: HoverCardRoot,
  Content: HoverCardContent,
});
